import * as THREE from "three";
import { ActDirector } from "./acts/ActDirector";
import { MusicDirector } from "./audio/MusicDirector";
import { fireFromEnemy } from "./enemies/EnemyFire";
import { Spawner, type EnemyKind, type EnemySpawn } from "./enemies/Spawner";
import { FakeFifo, FOUNDING_SEEDS, type HelperMessage } from "./help/FakeFifo";
import { ThumbSteer } from "./input/ThumbSteer";
import { RunState } from "./state/RunState";
import { circlesOverlap } from "./systems/Collision";

/**
 * 2D bullet-hell + light 3D polish.
 *
 * Combat ownership rules:
 * - Player bullets: only from player ship
 * - Helper bullets: only from living helpers
 * - Enemy bullets: only from living on-screen enemies (fireFromEnemy)
 * - No free-floating "sky volleys"
 */

type Bullet = {
  mesh: THREE.Mesh;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  fromPlayer: boolean;
  dead: boolean;
};

type Enemy = {
  mesh: THREE.Group;
  kind: EnemyKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  hp: number;
  r: number;
  dead: boolean;
  fireCd: number;
  fireInterval: number;
  spin: number;
};

type Helper = {
  mesh: THREE.Group;
  x: number;
  y: number;
  msg: HelperMessage;
  fireCd: number;
  /** When > 0, helper is down (hit absorbed) and will return */
  downTimer: number;
  active: boolean;
};

export type GameCallbacks = {
  onHud: (info: {
    actName: string;
    progress: number;
    deaths: number;
    helpers: number;
  }) => void;
  onHelpOffer: () => void;
  onHelperMessage: (msg: HelperMessage) => void;
  /** Dramatic Accept beat — UI freezes quotes then resolves */
  onRescueCinema?: (messages: HelperMessage[]) => Promise<void>;
  onClear: (result: {
    elapsed: number;
    helped: boolean;
    deaths: number;
  }) => void;
  onDeathForHelp: () => void;
};

export class GameApp {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene = new THREE.Scene();
  /** Orthographic — locks 2D play proportions */
  private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 200);
  private world = new THREE.Group();
  private steer = new ThumbSteer({ width: 390, height: 700, margin: 22 });
  private ship = { x: 195, y: 500 };
  private shipMesh: THREE.Group | null = null;
  private bullets: Bullet[] = [];
  private enemies: Enemy[] = [];
  private helpers: Helper[] = [];
  private run = new RunState();
  private acts = new ActDirector();
  private music = new MusicDirector(210);
  private spawner = new Spawner(390, 700);
  private fireCd = 0;
  private running = false;
  private pausedForHelp = false;
  private helpOffers = 0;
  /** Wiki: ~4 deaths before help is offered */
  private readonly deathsBeforeRescue = 4;
  /** First accept ≈ 6 pods (wiki); later smaller batches */
  private readonly maxHelpers = 12;
  /** Seconds a claimed helper stays down before returning */
  private readonly helperDownSeconds = 4.0;
  private w = 390;
  private h = 700;
  private fifo = new FakeFifo(FOUNDING_SEEDS);
  private clock = new THREE.Clock();
  private bgLayer: THREE.Group | null = null;
  private raf = 0;
  /** Screen pixels → world units (1 world unit ≈ 1 CSS px at scale 1) */
  private readonly unit = 1;

  // BHM / Ending E: high-contrast silhouettes on dark grid
  private matShip = new THREE.MeshBasicMaterial({ color: 0xf2f4f8 });
  private matShipAccent = new THREE.MeshBasicMaterial({ color: 0x5ec8ff });
  private matShipOutline = new THREE.MeshBasicMaterial({ color: 0x9aa3b2 });
  private matEnemy = new THREE.MeshBasicMaterial({ color: 0xff4d6a });
  private matEnemyHot = new THREE.MeshBasicMaterial({ color: 0xffb020 });
  private matEnemyCore = new THREE.MeshBasicMaterial({ color: 0xffe066 });
  private matBulletP = new THREE.MeshBasicMaterial({ color: 0xb8f0ff });
  private matBulletE = new THREE.MeshBasicMaterial({ color: 0xff5577 });
  private matHelper = new THREE.MeshBasicMaterial({ color: 0x7af0c8 });
  private matGrid = new THREE.LineBasicMaterial({
    color: 0x2a3344,
    transparent: true,
    opacity: 0.85,
  });
  private matGridMajor = new THREE.LineBasicMaterial({
    color: 0x3d4d66,
    transparent: true,
    opacity: 0.95,
  });
  private gridGroup: THREE.Group | null = null;
  private gridScroll = 0;

  constructor(
    private host: HTMLElement,
    private cb: GameCallbacks,
  ) {}

  async init(): Promise<void> {
    this.host.style.position = "relative";
    this.host.style.overflow = "hidden";
    this.host.style.touchAction = "none";

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x050508, 1);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    this.renderer = renderer;
    this.host.appendChild(renderer.domElement);
    Object.assign(renderer.domElement.style, {
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      display: "block",
    });

    // Dark grid field (BHM-style readable playfield) — no heavy fog
    this.scene.fog = null;
    this.scene.background = new THREE.Color(0x0b0e14);
    this.scene.add(this.world);

    // BasicMaterials don't need lights, but keep a dim ambient for any standard mats
    this.scene.add(new THREE.AmbientLight(0xffffff, 1));

    this.buildGridBackground();
    this.shipMesh = this.makePlayerShip();
    this.world.add(this.shipMesh);

    this.layout();
    this.bindInput(renderer.domElement);
    window.addEventListener("resize", () => this.layout());

    void this.music.load(["/audio/main.mp3", "/audio/loop.mp3"]);

    this.clock.start();
    const loop = () => {
      this.raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, this.clock.getDelta());
      try {
        this.update(dt);
        this.render();
      } catch (err) {
        console.error("[Ending E] frame error", err);
      }
    };
    loop();
    this.syncShipMesh();
    this.render();
  }

  private layout(): void {
    if (!this.renderer) return;
    const rect = this.host.getBoundingClientRect();
    const w = Math.max(320, Math.floor(rect.width || this.host.clientWidth || 390));
    const h = Math.max(480, Math.floor(rect.height || this.host.clientHeight || 700));
    this.w = w;
    this.h = h;
    this.renderer.setSize(w, h, false);
    this.renderer.domElement.style.width = "100%";
    this.renderer.domElement.style.height = "100%";

    // Orthographic frustum matches screen 1:1 in world units
    const halfW = (w * this.unit) / 2;
    const halfH = (h * this.unit) / 2;
    this.camera.left = -halfW;
    this.camera.right = halfW;
    this.camera.top = halfH;
    this.camera.bottom = -halfH;
    this.camera.near = 0.1;
    this.camera.far = 2000;
    this.camera.updateProjectionMatrix();
    this.fitOrthoCamera();

    this.steer.setField({ width: w, height: h, margin: 28 });
    this.spawner.setSize(w, h);
    // Rebuild grid when viewport size changes so lines fill the field
    this.buildGridBackground();

    if (!this.running) {
      this.ship.x = w / 2;
      this.ship.y = h * 0.72;
      this.syncShipMesh();
    }
  }

  /**
   * Nearly top-down orthographic — Ending E credit plane with a *tiny* fixed tilt
   * for light form only. Full playfield always framed. No chase cam.
   */
  private fitOrthoCamera(): void {
    const halfH = (this.h * this.unit) / 2;
    const elev = halfH * 2.4;
    const pull = halfH * 0.06; // almost top-down
    this.camera.position.set(0, elev, pull);
    this.camera.up.set(0, 1, 0);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
  }

  /**
   * Screen px (origin top-left, Y down) → world on XZ plane (Y up for mesh height).
   * Screen up = world −Z (classic vertical shmup).
   */
  private toWorld(x: number, y: number, lift = 0): THREE.Vector3 {
    const wx = (x - this.w / 2) * this.unit;
    const wz = (y - this.h / 2) * this.unit;
    return new THREE.Vector3(wx, lift, wz);
  }

  private bindInput(el: HTMLElement): void {
    const toLocal = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * this.w,
        y: ((e.clientY - rect.top) / rect.height) * this.h,
      };
    };
    el.addEventListener("pointerdown", (e) => {
      if (!this.running || this.pausedForHelp) return;
      void this.music.unlock();
      el.setPointerCapture(e.pointerId);
      const p = toLocal(e);
      // Bullet Hell Monday: grab ship relative to finger (no virtual stick)
      this.steer.onPointerDown(e.pointerId, p.x, p.y, this.ship);
    });
    el.addEventListener("pointermove", (e) => {
      if (!this.running || this.pausedForHelp) return;
      const p = toLocal(e);
      this.steer.onPointerMove(e.pointerId, p.x, p.y);
    });
    const up = (e: PointerEvent) => this.steer.onPointerUp(e.pointerId);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  }

  async startRun(): Promise<void> {
    this.layout();
    this.resetWorld();
    this.running = true;
    this.pausedForHelp = false;
    this.syncShipMesh();
    this.render();
    try {
      await this.music.play();
      if (!this.music.isPlaying) {
        await this.music.unlock();
        await this.music.play();
      }
    } catch (err) {
      console.warn("[Ending E] BGM play failed", err);
    }
  }

  stopRun(): void {
    this.running = false;
    this.music.stop();
  }

  private resetWorld(): void {
    for (const b of this.bullets) this.world.remove(b.mesh);
    this.bullets = [];
    for (const e of this.enemies) this.world.remove(e.mesh);
    this.enemies = [];
    for (const h of this.helpers) this.world.remove(h.mesh);
    this.helpers = [];
    this.run = new RunState();
    // Opening grace covers most of safeTime so first bullets don't insta-kill
    this.run.grantIFrames(2.0);
    this.acts = new ActDirector();
    this.spawner = new Spawner(this.w, this.h);
    this.spawner.reset();
    this.helpOffers = 0;
    this.fireCd = 0;
    this.ship.x = this.w / 2;
    this.ship.y = this.h * 0.72;
    this.steer.center();
    this.syncShipMesh();
  }

  /**
   * How many assist pods to pull this Accept.
   * Wiki: first wave ~6; later deaths can add more (capped).
   */
  helpPullCount(): number {
    const room = this.maxHelpers - this.helpers.length;
    if (room <= 0) return 0;
    const batch = this.helpOffers === 0 ? 6 : 3;
    return Math.min(batch, room);
  }

  /** Active (not down) helper count */
  private activeHelperCount(): number {
    return this.helpers.filter((h) => h.active && h.downTimer <= 0).length;
  }

  /**
   * Accept rescue: cinema first (freeze feel), then pods join.
   */
  async acceptHelp(remote?: HelperMessage[]): Promise<void> {
    const n = this.helpPullCount();
    const msgs =
      n <= 0
        ? []
        : remote && remote.length > 0
          ? remote.slice(0, n)
          : this.fifo.next(Math.max(n, 1));

    // Dramatic quote beat while world stays paused
    if (this.cb.onRescueCinema) {
      await this.cb.onRescueCinema(msgs.length ? msgs : this.fifo.next(3));
    }

    this.pausedForHelp = false;
    this.run.flags.acceptedHelp = true;
    this.run.grantIFrames(1.8);
    for (const b of this.bullets) {
      if (b.fromPlayer || b.dead) continue;
      if (Math.hypot(b.x - this.ship.x, b.y - this.ship.y) < 160) {
        b.dead = true;
      }
    }

    if (n > 0) {
      this.helpOffers += 1;
      const use = msgs.slice(0, n);
      use.forEach((msg, i) => {
        this.spawnHelper(
          msg,
          this.helpers.length + i,
          this.helpers.length + use.length,
        );
      });
    }
    this.running = true;
  }

  async refuseHelp(): Promise<void> {
    this.pausedForHelp = false;
    this.running = false;
    this.music.stop();
    await this.startRun();
  }

  /**
   * Soft continue after early deaths (1–3): no modal, brief invuln, local clear.
   */
  private softRespawn(): void {
    this.run.grantIFrames(1.6);
    for (const b of this.bullets) {
      if (b.fromPlayer || b.dead) continue;
      if (Math.hypot(b.x - this.ship.x, b.y - this.ship.y) < 140) {
        b.dead = true;
      }
    }
    this.running = true;
    this.pausedForHelp = false;
  }

  /** Wiki: hit claims a turret; it returns shortly */
  private absorbHitWithHelper(): boolean {
    const live = this.helpers.filter((h) => h.active && h.downTimer <= 0);
    if (live.length === 0) return false;
    const victim = live[live.length - 1]!;
    victim.downTimer = this.helperDownSeconds;
    victim.mesh.visible = false;
    this.run.grantIFrames(1.0);
    for (const b of this.bullets) {
      if (b.fromPlayer || b.dead) continue;
      if (Math.hypot(b.x - this.ship.x, b.y - this.ship.y) < 100) {
        b.dead = true;
      }
    }
    return true;
  }

  private update(dt: number): void {
    if (this.running && !this.pausedForHelp) {
      this.music.tick(dt);
      this.run.elapsed += dt;
      this.run.tickInvuln(dt);
      this.acts.setProgress(this.music.progress);

      this.ship = this.steer.step(this.ship, dt);
      this.syncShipMesh();

      this.fireCd -= dt;
      if (this.fireCd <= 0) {
        this.fireCd = 0.08;
        this.spawnBullet(this.ship.x, this.ship.y - 18, 0, -560, true, 3);
      }

      // Assist pods surround player; down helpers recover over time
      const activeHelpers = this.helpers.filter(
        (h) => h.active && h.downTimer <= 0,
      );
      const nH = Math.max(1, activeHelpers.length);
      this.helpers.forEach((h, i) => {
        if (h.downTimer > 0) {
          h.downTimer -= dt;
          if (h.downTimer <= 0) {
            h.downTimer = 0;
            h.mesh.visible = true;
          } else {
            return;
          }
        }
        if (!h.active) return;
        const slot = activeHelpers.indexOf(h);
        const ang =
          ((slot >= 0 ? slot : i) / nH) * Math.PI * 2 +
          this.run.elapsed * 0.55;
        const radius = 42 + (i % 3) * 10;
        const tx = this.ship.x + Math.cos(ang) * radius;
        const ty = this.ship.y + Math.sin(ang) * radius * 0.85 - 8;
        h.x += (tx - h.x) * (1 - Math.exp(-6 * dt));
        h.y += (ty - h.y) * (1 - Math.exp(-6 * dt));
        h.mesh.position.copy(this.toWorld(h.x, h.y, 2));
        h.fireCd -= dt;
        if (h.fireCd <= 0) {
          h.fireCd = 0.14;
          this.spawnBullet(h.x, h.y - 10, 0, -500, true, 2.5);
        }
      });

      // 1) Spawn enemies only (no free bullets)
      if (this.run.alive) {
        for (const spec of this.spawner.update(dt, this.acts)) {
          this.spawnEnemy(spec);
        }
      }

      // 2) Move living enemies + fire only from their positions
      for (const e of this.enemies) {
        if (e.dead) continue;
        e.x += e.vx * dt;
        e.y += e.vy * dt;
        e.x += Math.sin(this.run.elapsed * 2 + e.y * 0.02) * 12 * dt;
        e.spin += dt;
        e.mesh.position.copy(this.toWorld(e.x, e.y, 3));
        // No idle spin — face the play direction only
        e.mesh.rotation.set(0, 0, 0);

        // Off-screen → remove (cannot fire once gone)
        if (e.y > this.h + 40 || e.x < -50 || e.x > this.w + 50) {
          e.dead = true;
          continue;
        }

        // Only fire while on the playfield and alive
        if (e.y > 8 && e.y < this.h * 0.82) {
          e.fireCd -= dt;
          if (e.fireCd <= 0) {
            e.fireCd = e.fireInterval;
            const shots = fireFromEnemy({
              enemyX: e.x,
              enemyY: e.y,
              shipX: this.ship.x,
              shipY: this.ship.y,
              kind: e.kind,
              intensity: this.acts.intensity,
              phase: e.spin,
            });
            for (const s of shots) {
              this.spawnBullet(s.x, s.y, s.vx, s.vy, false, s.r);
            }
          }
        }
      }

      // 3) Integrate bullets
      for (const b of this.bullets) {
        if (b.dead) continue;
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        b.mesh.position.copy(
          this.toWorld(b.x, b.y, b.fromPlayer ? 4 : 3.5),
        );
        if (b.x < -40 || b.x > this.w + 40 || b.y < -40 || b.y > this.h + 40) {
          b.dead = true;
        }
      }

      // 4) Player bullets hit enemies → enemy dies → no more shots from them
      for (const b of this.bullets) {
        if (b.dead || !b.fromPlayer) continue;
        for (const e of this.enemies) {
          if (e.dead) continue;
          if (
            circlesOverlap(
              { x: b.x, y: b.y, r: b.r },
              { x: e.x, y: e.y, r: e.r },
            )
          ) {
            b.dead = true;
            e.hp -= 1;
            if (e.hp <= 0) e.dead = true;
            break;
          }
        }
      }

      const hurt = { x: this.ship.x, y: this.ship.y, r: 4 };
      if (this.run.invuln <= 0 && this.run.alive) {
        let hit = false;
        for (const b of this.bullets) {
          if (b.dead || b.fromPlayer) continue;
          if (
            circlesOverlap(hurt, {
              x: b.x,
              y: b.y,
              r: Math.max(2.5, b.r * 0.7),
            })
          ) {
            b.dead = true;
            hit = true;
            break;
          }
        }
        if (!hit) {
          for (const e of this.enemies) {
            if (e.dead) continue;
            if (
              circlesOverlap(hurt, {
                x: e.x,
                y: e.y,
                r: Math.min(12, e.r * 0.55),
              })
            ) {
              e.dead = true;
              hit = true;
              break;
            }
          }
        }
        if (hit) this.resolvePlayerHit();
      }

      this.gc();
      this.cb.onHud({
        actName: this.acts.current.name,
        progress: this.music.progress,
        deaths: this.run.flags.deaths,
        helpers: this.activeHelperCount(),
      });

      if (this.acts.finished && this.run.alive) {
        this.run.markClear();
        this.running = false;
        this.music.stop();
        this.cb.onClear({
          elapsed: this.run.elapsed,
          helped: this.run.flags.acceptedHelp,
          deaths: this.run.flags.deaths,
        });
      }
    }

    // Scroll grid downward (screen-up flight illusion)
    if (this.gridGroup) {
      this.gridScroll += dt * 55;
      const cell = 40;
      this.gridGroup.position.z = this.gridScroll % cell;
    }

    if (this.shipMesh) {
      const flash =
        this.run.invuln > 0 && Math.floor(this.run.invuln * 16) % 2 === 0;
      this.matShip.color.setHex(flash ? 0x5ec8ff : 0xf2f4f8);
    }
  }

  private render(): void {
    if (!this.renderer) return;
    this.renderer.render(this.scene, this.camera);
  }

  private gc(): void {
    if (this.bullets.length > 350 || this.bullets.some((b) => b.dead)) {
      const live: Bullet[] = [];
      for (const b of this.bullets) {
        if (b.dead) {
          this.world.remove(b.mesh);
          b.mesh.geometry.dispose();
        } else live.push(b);
      }
      this.bullets = live;
    }
    if (this.enemies.some((e) => e.dead)) {
      const live: Enemy[] = [];
      for (const e of this.enemies) {
        if (e.dead) this.world.remove(e.mesh);
        else live.push(e);
      }
      this.enemies = live;
    }
  }

  /**
   * Hit resolution (wiki-faithful ladder):
   * 1) Active helpers → one helper goes down temporarily (not player death)
   * 2) No helpers, deaths < 4 → soft respawn, keep fighting
   * 3) No helpers, deaths ≥ 4 → rescue modal
   */
  private resolvePlayerHit(): void {
    if (this.absorbHitWithHelper()) return;

    const result = this.run.hit();
    if (result !== "dead") return;

    this.running = false;
    this.steer.center();

    if (this.run.flags.deaths < this.deathsBeforeRescue) {
      // Early falls: continue sequence without full restart
      this.softRespawn();
      return;
    }

    // Offer rescue (can accept again later if they die after helpers are gone)
    this.pausedForHelp = true;
    this.cb.onDeathForHelp();
    this.cb.onHelpOffer();
  }

  private syncShipMesh(): void {
    if (!this.shipMesh) return;
    this.shipMesh.position.copy(this.toWorld(this.ship.x, this.ship.y, 3));
    // Flat on plane; nose already points screen-up (−Z)
    this.shipMesh.rotation.set(0, 0, 0);
  }

  /**
   * Ending E / BHM-scale player: small flat arrow, nose = screen UP (−Z).
   * ~28px tip-to-tail on a ~390px phone width (classic mobile shmup scale).
   */
  private makePlayerShip(): THREE.Group {
    const g = new THREE.Group();
    // Shape in XY: tip at +Y. After rot.x = -90°: tip → −Z (screen up). Fixed backward bug.
    const bodyShape = new THREE.Shape();
    bodyShape.moveTo(0, 14); // nose (forward / screen-up)
    bodyShape.lineTo(10, -10);
    bodyShape.lineTo(0, -5);
    bodyShape.lineTo(-10, -10);
    bodyShape.closePath();
    const body = new THREE.Mesh(
      new THREE.ExtrudeGeometry(bodyShape, {
        depth: 2,
        bevelEnabled: false,
      }),
      this.matShip,
    );
    body.rotation.x = -Math.PI / 2;
    body.position.y = 1;

    // Outline ring for contrast on grid
    const outline = new THREE.Mesh(
      new THREE.RingGeometry(11, 12.5, 3, 1, Math.PI / 2, Math.PI * 2),
      this.matShipOutline,
    );
    outline.rotation.x = -Math.PI / 2;
    outline.position.set(0, 0.5, 1);
    outline.visible = false; // keep simple silhouette

    // Engine tab at rear (+Z = screen down)
    const eng = new THREE.Mesh(
      new THREE.BoxGeometry(4, 1.5, 5),
      this.matShipAccent,
    );
    eng.position.set(0, 1.2, 11);

    // Hitbox marker (tiny cyan core) — BHM-style readable center
    const core = new THREE.Mesh(
      new THREE.CircleGeometry(2.4, 10),
      this.matShipAccent,
    );
    core.rotation.x = -Math.PI / 2;
    core.position.set(0, 2.2, 1);

    g.add(body, eng, core, outline);
    return g;
  }

  /**
   * BHM-readable enemies: larger than player, high-contrast warm colors on dark grid.
   * Drone ~32px, glyph ~40px, warden ~48px.
   */
  private makeEnemyMesh(kind: EnemyKind): THREE.Group {
    const g = new THREE.Group();
    if (kind === "drone") {
      // Inverted chevron pointing down (toward player)
      const shape = new THREE.Shape();
      shape.moveTo(0, -14);
      shape.lineTo(12, 12);
      shape.lineTo(0, 6);
      shape.lineTo(-12, 12);
      shape.closePath();
      const m = new THREE.Mesh(
        new THREE.ExtrudeGeometry(shape, { depth: 2, bevelEnabled: false }),
        this.matEnemy,
      );
      m.rotation.x = -Math.PI / 2;
      m.position.y = 1;
      const rim = new THREE.Mesh(
        new THREE.CircleGeometry(5, 8),
        this.matEnemyCore,
      );
      rim.rotation.x = -Math.PI / 2;
      rim.position.set(0, 2.5, 0);
      g.add(m, rim);
    } else if (kind === "glyph") {
      const m = new THREE.Mesh(
        new THREE.BoxGeometry(28, 2.5, 28),
        this.matEnemyHot,
      );
      m.position.y = 1.2;
      const cross = new THREE.Mesh(
        new THREE.BoxGeometry(18, 2.8, 5),
        this.matEnemy,
      );
      cross.position.y = 1.5;
      const core = new THREE.Mesh(
        new THREE.CircleGeometry(6, 8),
        this.matEnemyCore,
      );
      core.rotation.x = -Math.PI / 2;
      core.position.y = 2.8;
      g.add(m, cross, core);
    } else {
      const m = new THREE.Mesh(
        new THREE.CylinderGeometry(18, 18, 3, 6),
        this.matEnemyHot,
      );
      m.position.y = 1.5;
      const core = new THREE.Mesh(
        new THREE.CircleGeometry(8, 8),
        this.matEnemyCore,
      );
      core.rotation.x = -Math.PI / 2;
      core.position.y = 3.2;
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(14, 17, 6),
        this.matEnemy,
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 3;
      g.add(m, core, ring);
    }
    return g;
  }

  private spawnEnemy(spec: EnemySpawn): void {
    const mesh = this.makeEnemyMesh(spec.kind);
    const r =
      spec.kind === "warden" ? 18 : spec.kind === "glyph" ? 16 : 14;
    mesh.position.copy(this.toWorld(spec.x, spec.y, 3));
    this.world.add(mesh);
    this.enemies.push({
      mesh,
      kind: spec.kind,
      x: spec.x,
      y: spec.y,
      vx: spec.vx,
      vy: spec.vy,
      hp: spec.hp,
      r,
      dead: false,
      fireCd: spec.fireDelay,
      fireInterval: spec.fireInterval,
      spin: Math.random() * Math.PI * 2,
    });
  }

  private spawnHelper(msg: HelperMessage, index: number, _total: number): void {
    const mesh = this.makePlayerShip();
    mesh.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.material = this.matHelper;
      }
    });
    mesh.scale.setScalar(0.78);
    const ang = (index / Math.max(1, this.maxHelpers)) * Math.PI * 2;
    const x = this.ship.x + Math.cos(ang) * 48;
    const y = this.ship.y + Math.sin(ang) * 40 - 8;
    mesh.position.copy(this.toWorld(x, y, 3));
    this.world.add(mesh);
    this.helpers.push({
      mesh,
      x,
      y,
      msg,
      fireCd: 0.08 * index,
      downTimer: 0,
      active: true,
    });
    this.run.flags.helpersJoined += 1;
  }

  private spawnBullet(
    x: number,
    y: number,
    vx: number,
    vy: number,
    fromPlayer: boolean,
    r: number,
  ): void {
    // Bright discs — high contrast on grid
    const geo = new THREE.CircleGeometry(fromPlayer ? 3.2 : 4.5, 12);
    const mesh = new THREE.Mesh(
      geo,
      fromPlayer ? this.matBulletP : this.matBulletE,
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.copy(this.toWorld(x, y, fromPlayer ? 4 : 3.5));
    this.world.add(mesh);
    this.bullets.push({ mesh, x, y, vx, vy, r, fromPlayer, dead: false });
  }

  /** Scrolling square grid — BHM-style readable field */
  private buildGridBackground(): void {
    if (this.gridGroup) {
      this.world.remove(this.gridGroup);
    }
    const g = new THREE.Group();
    const cell = 40;
    const halfW = Math.max(this.w, 400) * 0.7;
    const halfH = Math.max(this.h, 700) * 0.75;
    const ptsMinor: number[] = [];
    const ptsMajor: number[] = [];

    for (let x = -halfW; x <= halfW; x += cell) {
      const isMajor = Math.round(x / cell) % 4 === 0;
      const bucket = isMajor ? ptsMajor : ptsMinor;
      bucket.push(x, 0, -halfH, x, 0, halfH);
    }
    for (let z = -halfH; z <= halfH; z += cell) {
      const isMajor = Math.round(z / cell) % 4 === 0;
      const bucket = isMajor ? ptsMajor : ptsMinor;
      bucket.push(-halfW, 0, z, halfW, 0, z);
    }

    const mkLines = (arr: number[], mat: THREE.LineBasicMaterial) => {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(new Float32Array(arr), 3),
      );
      return new THREE.LineSegments(geo, mat);
    };
    g.add(mkLines(ptsMinor, this.matGrid));
    g.add(mkLines(ptsMajor, this.matGridMajor));

    // Floor fill under grid for solid contrast base
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(halfW * 2.2, halfH * 2.2),
      new THREE.MeshBasicMaterial({ color: 0x0b0e14 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.5;
    g.add(floor);

    this.gridGroup = g;
    this.bgLayer = g;
    this.world.add(g);
  }

  destroy(): void {
    this.stopRun();
    cancelAnimationFrame(this.raf);
    this.renderer?.dispose();
    this.renderer?.domElement.remove();
  }
}
